import { useCallback, useEffect, useRef, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../components/ui/dialog";
import { StatusBadge } from "../components/StatusBadge";
import Pagination from "../components/Pagination";
import { Upload, Search, Copy, Trash2, RotateCcw, Check, Download, AlertTriangle, Unlock } from "lucide-react";
import { toast } from "sonner";
import { TableSkeleton } from "../components/Skeletons";

const PAGE_SIZE = 20;

export default function ApiAccounts() {
  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 0, limit: PAGE_SIZE });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [uploadedBytes, setUploadedBytes] = useState(0);
  const [totalBytes, setTotalBytes] = useState(0);
  const [revealed, setRevealed] = useState({});
  const inputRef = useRef(null);

  // Export
  const [exporting, setExporting] = useState(false);

  // Delete-all
  const [delDialogOpen, setDelDialogOpen] = useState(false);
  const [delPassword, setDelPassword] = useState("");
  const [deleting, setDeleting] = useState(false);

  const fetchAccounts = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: PAGE_SIZE };
      if (statusFilter !== "all") params.status = statusFilter;
      if (q) params.q = q;
      const res = await api.get("/admin/accounts", { params });
      setData(res.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, q, page]);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  // Reset para página 1 quando filtros mudam
  useEffect(() => { setPage(1); }, [statusFilter, q]);

  const handleFile = async (file) => {
    if (!file) return;
    setUploading(true);
    setUploadPct(0);
    setUploadedBytes(0);
    setTotalBytes(file.size || 0);
    try {
      // Upload em pedaços de 2MB — passa por qualquer proxy/CDN sem travar
      const CHUNK_SIZE = 2 * 1024 * 1024;
      const uploadId = (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`).replace(/[^a-zA-Z0-9-]/g, "");
      let sentBase = 0;
      for (let offset = 0; offset < file.size; offset += CHUNK_SIZE) {
        const blob = file.slice(offset, offset + CHUNK_SIZE);
        const form = new FormData();
        form.append("upload_id", uploadId);
        form.append("chunk", blob, "chunk.bin");
        await api.post("/admin/accounts/upload-chunk", form, {
          headers: { "Content-Type": "multipart/form-data" },
          maxContentLength: Infinity,
          maxBodyLength: Infinity,
          timeout: 0,
          onUploadProgress: (e) => {
            const loaded = Math.min(sentBase + (e.loaded || 0), file.size);
            setUploadedBytes(loaded);
            if (file.size > 0) setUploadPct(Math.min(100, Math.round((loaded / file.size) * 100)));
          },
        });
        sentBase += blob.size;
        setUploadedBytes(Math.min(sentBase, file.size));
        setUploadPct(Math.min(100, Math.round((sentBase / file.size) * 100)));
      }
      // Finaliza: servidor processa o arquivo montado (streaming + lotes)
      const res = await api.post("/admin/accounts/upload-finish", { upload_id: uploadId }, { timeout: 0 });
      const { inserted = 0, updated = 0, total = 0, failed = 0 } = res.data || {};
      const parts = [`${inserted} adicionadas`, `${updated} atualizadas`];
      if (failed > 0) parts.push(`${failed} falhas`);
      toast.success(`Upload concluído (${total} processadas): ${parts.join(", ")}`);
      setOpen(false);
      setPage(1);
      fetchAccounts();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
      setUploadPct(0);
      setUploadedBytes(0);
      setTotalBytes(0);
    }
  };

  const formatBytes = (n) => {
    if (!n && n !== 0) return "";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
    return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  };

  const copy = async (text, id) => {
    await navigator.clipboard.writeText(text);
    setRevealed((r) => ({ ...r, [id]: true }));
    toast.success("Copiado para a área de transferência");
    setTimeout(() => setRevealed((r) => ({ ...r, [id]: false })), 1500);
  };

  const release = async (id) => {
    try {
      await api.post(`/admin/accounts/${id}/release`);
      toast.success("Conta liberada");
      fetchAccounts();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Remover esta conta definitivamente?")) return;
    try {
      await api.delete(`/admin/accounts/${id}`);
      toast.success("Conta removida");
      fetchAccounts();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const maskKey = (k) => (k ? `${k.slice(0, 6)}${"•".repeat(12)}${k.slice(-4)}` : "—");

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await api.get("/admin/accounts/export", {
        responseType: "blob",
        timeout: 0,
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
      });
      // Pega filename do header (se vier)
      let filename = "contas-api.json";
      const cd = res.headers?.["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      if (m && m[1]) filename = m[1];

      const blob = res.data instanceof Blob ? res.data : new Blob([res.data], { type: "application/json" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success(`Exportado: ${filename}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteAll = async () => {
    if (!delPassword.trim()) {
      toast.error("Informe a senha de segurança");
      return;
    }
    setDeleting(true);
    try {
      const res = await api.post("/admin/accounts/delete-all", { password: delPassword });
      toast.success(res.data?.message || "Todas as contas foram excluídas");
      setDelDialogOpen(false);
      setDelPassword("");
      setPage(1);
      fetchAccounts();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDeleting(false);
    }
  };

  const [releasingAll, setReleasingAll] = useState(false);
  const handleReleaseAllReserved = async () => {
    if (!window.confirm("Liberar TODAS as contas atualmente reservadas?\n\nElas voltarão para o status 'Disponível'.")) return;
    setReleasingAll(true);
    try {
      const res = await api.post("/admin/accounts/release-all-reserved");
      const n = res.data?.released ?? 0;
      if (n === 0) {
        toast.info("Não havia nenhuma conta reservada");
      } else {
        toast.success(`${n.toLocaleString("pt-BR")} conta(s) liberada(s)`);
      }
      fetchAccounts();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setReleasingAll(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="accounts-page">
      <div className="flex flex-wrap items-center justify-end gap-2">
        <Button
          onClick={handleExport}
          variant="outline"
          disabled={exporting || data.total === 0}
          className="h-10 rounded-md"
          data-testid="export-json-button"
        >
          <Download className="h-4 w-4 mr-2" />
          {exporting ? "Exportando…" : "Exportar JSON"}
        </Button>
        <Button
          onClick={() => setOpen(true)}
          className="h-10 bg-slate-900 hover:bg-slate-800 text-white rounded-md transition-colors duration-200"
          data-testid="upload-json-button"
        >
          <Upload className="h-4 w-4 mr-2" /> Upload JSON
        </Button>
        <Button
          onClick={handleReleaseAllReserved}
          disabled={releasingAll || data.total === 0}
          variant="outline"
          className="h-10 rounded-md border-emerald-200 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800"
          data-testid="release-all-reserved-button"
          title="Liberar todas as contas atualmente reservadas"
        >
          <Unlock className={`h-4 w-4 mr-2 ${releasingAll ? "animate-pulse" : ""}`} />
          {releasingAll ? "Liberando…" : "Liberar reservadas"}
        </Button>
        <Button
          onClick={() => { setDelPassword(""); setDelDialogOpen(true); }}
          disabled={data.total === 0}
          className="h-10 bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors duration-200"
          data-testid="delete-all-button"
        >
          <Trash2 className="h-4 w-4 mr-2" /> Excluir todas
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px] sm:max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            placeholder="Buscar por email…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="pl-9 h-10 rounded-md"
            data-testid="search-input"
          />
        </div>
        <div className="inline-flex rounded-md border border-slate-200 bg-white p-1">
          {[
            { v: "all", label: "Todas" },
            { v: "Disponível", label: "Disponíveis" },
            { v: "Reservado", label: "Reservadas" },
            { v: "Suspensa", label: "Suspensas" },
          ].map((f) => (
            <button
              key={f.v}
              onClick={() => setStatusFilter(f.v)}
              data-testid={`filter-${f.v}`}
              className={`px-3 py-1.5 text-xs rounded transition-colors duration-200 ${
                statusFilter === f.v
                  ? "bg-slate-900 text-white"
                  : "text-slate-600 hover:bg-slate-50"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead>
              <tr className="text-left bg-slate-50 border-b border-slate-200">
                {["Email", "Status", "Nome da Chave", "Chave da API", "Criada em", "Reservada até", "Ações"].map((h) => (
                  <th key={h} className="px-5 py-3 text-[11px] uppercase tracking-[0.18em] font-medium text-slate-500">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="p-0"><TableSkeleton columns={7} rows={8} /></td></tr>
              ) : data.items.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-14 text-center" data-testid="empty-state">
                    <p className="text-sm text-slate-600 font-medium">Nenhuma conta encontrada</p>
                    <p className="text-xs text-slate-400 mt-1">Faça upload de um JSON para começar ou ajuste os filtros.</p>
                  </td>
                </tr>
              ) : (
                data.items.map((a, i) => (
                  <tr
                    key={a.id}
                    className="border-b border-slate-100 hover:bg-slate-50/60"
                    data-testid={`account-row-${a.id}`}
                  >
                    <td className="px-5 py-3 font-medium text-slate-900">{a.email}</td>
                    <td className="px-5 py-3"><StatusBadge status={a.status} /></td>
                    <td className="px-5 py-3 text-slate-700">{a.api_key_info?.keyName}</td>
                    <td className="px-5 py-3">
                      <div className="inline-flex items-center gap-2">
                        <code className="mono text-xs text-slate-700 bg-slate-50 border border-slate-200 rounded px-2 py-1">
                          {maskKey(a.api_key_info?.apiKey)}
                        </code>
                        <button
                          onClick={() => copy(a.api_key_info?.apiKey || "", a.id)}
                          className="p-1.5 text-slate-500 hover:text-slate-900 hover:bg-slate-100 rounded transition-colors"
                          title="Copiar chave da API"
                          data-testid={`copy-key-${a.id}`}
                        >
                          {revealed[a.id] ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
                        </button>
                      </div>
                    </td>
                    <td className="px-5 py-3 text-slate-500 text-xs">
                      {a.created_at ? new Date(a.created_at).toLocaleString("pt-BR") : "—"}
                    </td>
                    <td className="px-5 py-3 text-slate-500 text-xs">
                      {a.reserved_until ? new Date(a.reserved_until).toLocaleString("pt-BR") : "—"}
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-1">
                        {(a.status === "Reservado" || a.status === "Suspensa") && (
                          <button
                            onClick={() => release(a.id)}
                            className="p-1.5 text-slate-500 hover:text-emerald-700 hover:bg-emerald-50 rounded transition-colors"
                            title={a.status === "Suspensa" ? "Reativar (voltar a Disponível)" : "Liberar agora"}
                            data-testid={`release-${a.id}`}
                          >
                            <RotateCcw className="h-3.5 w-3.5" />
                          </button>
                        )}
                        <button
                          onClick={() => remove(a.id)}
                          className="p-1.5 text-slate-500 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
                          title="Remover"
                          data-testid={`delete-${a.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {!loading && data.total > 0 && (
          <Pagination
            page={data.page}
            pages={data.pages}
            total={data.total}
            limit={data.limit}
            onChange={setPage}
          />
        )}
      </div>

      {/* Dialog de Upload */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="upload-dialog">
          <DialogHeader>
            <DialogTitle>Upload de contas via JSON</DialogTitle>
            <DialogDescription>
              Envie um arquivo <code className="mono">.json</code> contendo um array de contas. Contas existentes (mesmo <code className="mono">id</code>) serão atualizadas. <strong>Sem limite de tamanho</strong> — arquivos grandes são processados em streaming com gravação em lotes.
            </DialogDescription>
          </DialogHeader>

          <div
            onDragOver={(e) => {
              e.preventDefault();
              setDragOver(true);
            }}
            onDragLeave={() => setDragOver(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragOver(false);
              const f = e.dataTransfer.files?.[0];
              if (f) handleFile(f);
            }}
            onClick={() => inputRef.current?.click()}
            className={`mt-2 border-2 border-dashed rounded-lg p-10 text-center cursor-pointer transition-colors duration-200 ${
              dragOver ? "border-slate-900 bg-slate-50" : "border-slate-300 hover:border-slate-500 hover:bg-slate-50"
            }`}
            data-testid="upload-dropzone"
          >
            <Upload className="h-6 w-6 mx-auto text-slate-400" />
            <p className="mt-3 text-sm font-medium text-slate-900">Arraste o JSON aqui ou clique para selecionar</p>
            <p className="mt-1 text-xs text-slate-500">Array no mesmo formato do exemplo (id, email, password, authorization_bearer, api_key_info)</p>
            <input
              ref={inputRef}
              type="file"
              accept=".json,application/json"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
              data-testid="file-input"
            />
          </div>

          {uploading && (
            <div className="mt-4 space-y-2" data-testid="upload-progress">
              <div className="flex items-center justify-between text-xs text-slate-600">
                <span className="font-medium">Enviando arquivo…</span>
                <span className="mono">
                  {formatBytes(uploadedBytes)}
                  {totalBytes > 0 ? ` / ${formatBytes(totalBytes)}` : ""}
                  {totalBytes > 0 ? ` (${uploadPct}%)` : ""}
                </span>
              </div>
              <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-slate-900 transition-all duration-200"
                  style={{ width: `${totalBytes > 0 ? uploadPct : 100}%` }}
                />
              </div>
              {uploadPct === 100 && (
                <p className="text-[11px] text-slate-500">
                  Upload concluído. Processando no servidor (pode levar alguns minutos para arquivos muito grandes)…
                </p>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={uploading}>Cancelar</Button>
            <Button
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              className="bg-slate-900 hover:bg-slate-800 text-white"
              data-testid="pick-file-button"
            >
              {uploading ? "Enviando…" : "Selecionar arquivo"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog: Excluir TODAS as contas com senha de segurança */}
      <Dialog open={delDialogOpen} onOpenChange={(o) => { if (!deleting) setDelDialogOpen(o); }}>
        <DialogContent className="sm:max-w-md" data-testid="delete-all-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="h-5 w-5" />
              Excluir TODAS as contas
            </DialogTitle>
            <DialogDescription>
              Essa ação <strong>não pode ser desfeita</strong>. Vai apagar permanentemente
              <strong> {data.total} </strong>conta(s) do banco. Para confirmar, informe a
              <strong> senha de segurança</strong> configurada no servidor (variável de ambiente
              <code className="mono"> ACCOUNTS_DELETE_PASSWORD</code>).
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2 py-2">
            <label className="text-xs font-medium text-slate-600 uppercase tracking-wider">
              Senha de segurança
            </label>
            <Input
              type="password"
              autoFocus
              autoComplete="off"
              value={delPassword}
              onChange={(e) => setDelPassword(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !deleting) handleDeleteAll(); }}
              placeholder="Digite a senha…"
              disabled={deleting}
              className="mono"
              data-testid="delete-all-password-input"
            />
            <p className="text-[11px] text-slate-500">
              Dica: use o mesmo valor definido em <code className="mono">backend/.env</code> →
              <code className="mono"> ACCOUNTS_DELETE_PASSWORD</code>.
            </p>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDelDialogOpen(false)} disabled={deleting}>
              Cancelar
            </Button>
            <Button
              onClick={handleDeleteAll}
              disabled={deleting || !delPassword.trim()}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-delete-all-button"
            >
              {deleting ? "Excluindo…" : `Excluir ${data.total} conta(s)`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
